import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { environment } from '../config/environment';
import { getApiHeadersForGet } from '../utils/apiUtils';
import { getCompleteImagePath } from '../utils/imageUtils';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Tooltip,
  IconButton,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  FileDownload as DownloadIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelIcon,
  Sync as ProcessingIcon,
  PlayArrow as ExecuteIcon,
  Person as UserIcon,
  PersonOff as GuestIcon
} from '@mui/icons-material';

// ─── Types ────────────────────────────────────────────────────────────────────

type PayoutStatus = 'pending' | 'processing' | 'completed' | 'cancelled';
type PayoutType   = 'cash' | 'gift_card' | 'rewards_points' | '';

interface Winner {
  id: string;
  participant_name: string;
  participant_id: string | null;
  user_id: string | null;
  participant_email: string | null;
  votes: number;
  photo_url: string | null;
  place: number;
  reward: string | number;
  payout_status: PayoutStatus;
  payout_type: PayoutType;
  payout_notes: string | null;
  payout_date: string | null;
  winner_email_sent: number;
  created_at: string;
}

interface ContestWinners {
  contest_id: string;
  contest_name: string;
  contest_status: string;
  winners: Winner[];
}

interface WinnersResponse {
  success: boolean;
  total_contests: number;
  total_winners: number;
  contests: ContestWinners[];
}

interface PayoutFormState {
  payout_status: PayoutStatus;
  payout_type: PayoutType;
  payout_notes: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAuthHeaders = () => {
  const base = getApiHeadersForGet() as Record<string, string>;
  return { ...base, 'Content-Type': 'application/json' };
};

const formatReward = (reward: string | number): string => {
  if (typeof reward === 'number') return `$${reward.toFixed(2)}`;
  if (typeof reward === 'string') {
    if (reward.includes('$')) return reward;
    const num = parseFloat(reward);
    if (!isNaN(num)) return `$${num.toFixed(2)}`;
    return reward;
  }
  return '$0.00';
};

const placeLabel = (place: number): string => {
  if (place === 1) return '1st';
  if (place === 2) return '2nd';
  if (place === 3) return '3rd';
  return `${place}th`;
};

const placeColor = (place: number): 'warning' | 'default' | 'secondary' | 'primary' => {
  if (place === 1) return 'warning';
  if (place === 2) return 'default';
  if (place === 3) return 'secondary';
  return 'primary';
};

const payoutStatusConfig: Record<PayoutStatus, { label: string; color: 'default' | 'warning' | 'success' | 'error'; icon: React.ReactNode }> = {
  pending:    { label: 'Pending',    color: 'warning', icon: <PendingIcon    fontSize="small" /> },
  processing: { label: 'Processing', color: 'default', icon: <ProcessingIcon fontSize="small" /> },
  completed:  { label: 'Completed',  color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  cancelled:  { label: 'Cancelled',  color: 'error',   icon: <CancelIcon     fontSize="small" /> }
};

const payoutTypeLabels: Record<string, string> = {
  cash:           'Cash',
  gift_card:      'Gift Card',
  rewards_points: 'Reward Points',
  '':             '—'
};

// ─── CSV export ───────────────────────────────────────────────────────────────

const exportCsv = (contests: ContestWinners[]) => {
  const rows: string[][] = [
    ['Contest', 'Place', 'Participant', 'Votes', 'Reward', 'Payout Status', 'Payout Type', 'Payout Date', 'Notes', 'Email Sent']
  ];
  contests.forEach(c => {
    c.winners.forEach(w => {
      rows.push([
        c.contest_name,
        String(w.place),
        w.participant_name,
        String(w.votes),
        String(w.reward),
        w.payout_status,
        w.payout_type || '',
        w.payout_date || '',
        w.payout_notes || '',
        w.winner_email_sent ? 'Yes' : 'No'
      ]);
    });
  });
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `winners-payout-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Component ────────────────────────────────────────────────────────────────

interface ContestWinnersPageProps {
  embedded?: boolean;
}

const ContestWinnersPage: React.FC<ContestWinnersPageProps> = ({ embedded = false }) => {
  const navigate = useNavigate();

  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [winnersData, setWinnersData]   = useState<WinnersResponse | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | PayoutStatus>('all');
  const [saving, setSaving]             = useState<Record<string, boolean>>({});
  const [executing, setExecuting]       = useState<Record<string, boolean>>({});
  const [resending, setResending]       = useState<Record<string, boolean>>({});
  const [editState, setEditState]       = useState<Record<string, PayoutFormState>>({});
  const [snackbar, setSnackbar]         = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  const loadWinners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/winners`, {
        headers: getApiHeadersForGet() as HeadersInit
      });
      if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
      const data: WinnersResponse = await response.json();
      setWinnersData(data);
    } catch (err: any) {
      setError(err.message || 'Error loading winners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWinners(); }, [loadWinners]);

  // Initialise edit state from server data
  useEffect(() => {
    if (!winnersData) return;
    setEditState(prev => {
      const initial: Record<string, PayoutFormState> = {};
      winnersData.contests.forEach(c =>
        c.winners.forEach(w => {
          if (!prev[w.id]) {
            initial[w.id] = {
              payout_status: w.payout_status || 'pending',
              payout_type:   w.payout_type   || '',
              payout_notes:  w.payout_notes  || ''
            };
          }
        })
      );
      if (Object.keys(initial).length === 0) return prev;
      return { ...initial, ...prev };
    });
    const initial: Record<string, PayoutFormState> = {};
    winnersData.contests.forEach(c =>
      c.winners.forEach(w => {
        if (!editState[w.id]) {
          initial[w.id] = {
            payout_status: w.payout_status || 'pending',
            payout_type:   w.payout_type   || '',
            payout_notes:  w.payout_notes  || ''
          };
        }
      })
    );
    if (Object.keys(initial).length > 0) {
      setEditState(prev => ({ ...initial, ...prev }));
    }
  }, [winnersData]);

  const handleSavePayout = async (winnerId: string) => {
    const form = editState[winnerId];
    if (!form) return;
    setSaving(s => ({ ...s, [winnerId]: true }));
    try {
      const response = await fetch(`${environment.apiBaseUrl}/winners/${winnerId}/payout`, {
        method: 'PATCH',
        headers: getAuthHeaders() as HeadersInit,
        body: JSON.stringify({
          payout_status: form.payout_status,
          payout_type:   form.payout_type   || null,
          payout_notes:  form.payout_notes  || null
        })
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      setSnackbar({ open: true, message: 'Payout updated successfully', severity: 'success' });
      await loadWinners();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error saving payout', severity: 'error' });
    } finally {
      setSaving(s => ({ ...s, [winnerId]: false }));
    }
  };

  /**
   * Execute Payout — saves form with payout_status='completed' which triggers
   * the execute-payout webhook in pet-contests → pawwebapp.
   */
  const handleExecutePayout = async (winnerId: string) => {
    const form = editState[winnerId];
    if (!form) return;
    if (!form.payout_type) {
      setSnackbar({ open: true, message: 'Please select a Payout Type before executing', severity: 'error' });
      return;
    }
    setExecuting(e => ({ ...e, [winnerId]: true }));
    try {
      const response = await fetch(`${environment.apiBaseUrl}/winners/${winnerId}/payout`, {
        method: 'PATCH',
        headers: getAuthHeaders() as HeadersInit,
        body: JSON.stringify({
          payout_status: 'completed',
          payout_type:   form.payout_type,
          payout_notes:  form.payout_notes || null
        })
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const result = await response.json().catch(() => ({}));
      setSnackbar({
        open: true,
        message: result.payout_webhook_triggered === false
          ? `Payout saved locally but webhook is not configured on pet-contests`
          : `Payout executed (${form.payout_type}) — crediting in background`,
        severity: result.payout_webhook_triggered === false ? 'error' : 'success',
      });
      setEditState(prev => ({ ...prev, [winnerId]: { ...form, payout_status: 'completed' } }));
      await loadWinners();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error executing payout', severity: 'error' });
    } finally {
      setExecuting(e => ({ ...e, [winnerId]: false }));
    }
  };

  /** Re-fire pawwebapp execute-payout when status is already completed but credit failed */
  const handleRetryPayout = async (winnerId: string) => {
    const form = editState[winnerId];
    if (!form?.payout_type) {
      setSnackbar({ open: true, message: 'Select a Payout Type before retrying', severity: 'error' });
      return;
    }
    setExecuting(e => ({ ...e, [winnerId]: true }));
    try {
      const response = await fetch(`${environment.apiBaseUrl}/winners/${winnerId}/payout`, {
        method: 'PATCH',
        headers: getAuthHeaders() as HeadersInit,
        body: JSON.stringify({
          payout_status: 'completed',
          payout_type:   form.payout_type,
          payout_notes:  form.payout_notes || null,
          force: true,
        })
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const result = await response.json().catch(() => ({}));
      setSnackbar({
        open: true,
        message: result.payout_webhook_triggered === false
          ? 'Retry saved but webhook is not configured on pet-contests'
          : 'Payout retry sent to pawwebapp',
        severity: result.payout_webhook_triggered === false ? 'error' : 'success',
      });
      await loadWinners();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error retrying payout', severity: 'error' });
    } finally {
      setExecuting(e => ({ ...e, [winnerId]: false }));
    }
  };

  const handleResendEmail = async (winnerId: string) => {
    setResending(r => ({ ...r, [winnerId]: true }));
    try {
      const response = await fetch(`${environment.apiBaseUrl}/winners/${winnerId}/resend-email`, {
        method: 'POST',
        headers: getAuthHeaders() as HeadersInit
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      setSnackbar({ open: true, message: 'Winner email resent successfully', severity: 'success' });
      await loadWinners();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error resending email', severity: 'error' });
    } finally {
      setResending(r => ({ ...r, [winnerId]: false }));
    }
  };

  // ── Filtered contests ──────────────────────────────────────────────────────

  const filteredContests = (winnersData?.contests ?? [])
    .map(c => ({
      ...c,
      winners: filterStatus === 'all'
        ? c.winners
        : c.winners.filter(w => (w.payout_status || 'pending') === filterStatus)
    }))
    .filter(c => c.winners.length > 0);

  const totalPending   = winnersData?.contests.flatMap(c => c.winners).filter(w => (w.payout_status || 'pending') === 'pending').length   ?? 0;
  const totalCompleted = winnersData?.contests.flatMap(c => c.winners).filter(w => w.payout_status === 'completed').length ?? 0;

  // ── Render guards ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box className="napc-page" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: embedded ? 240 : '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container className="napc-page" maxWidth="lg" sx={{ py: embedded ? 0 : 4, minHeight: embedded ? 'auto' : '100vh' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        {!embedded && (
          <Chip icon={<ArrowBackIcon />} label="Back" onClick={() => navigate(-1)} color="primary" clickable />
        )}
      </Container>
    );
  }

  const content = (
    <>
      {!embedded && (
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: 3 }}>
            <Box>
              <Box className="napc-eyebrow">Payout Management</Box>
              <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0.5 }}>
                <TrophyIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2rem' }} />
                Contest Winners
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {winnersData?.total_winners ?? 0} winners across {winnersData?.total_contests ?? 0} contests
                {' · '}
                <strong>{totalPending}</strong> pending payout
                {' · '}
                <strong>{totalCompleted}</strong> completed
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Refresh">
                <IconButton onClick={loadWinners} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              {winnersData && winnersData.total_winners > 0 && (
                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => exportCsv(winnersData.contests)}
                >
                  Export CSV
                </Button>
              )}
              <Chip icon={<ArrowBackIcon />} label="Back" onClick={() => navigate(-1)} color="primary" clickable />
            </Box>
          </Box>
        </>
      )}

      {embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {winnersData?.total_winners ?? 0} winners · <strong>{totalPending}</strong> pending · <strong>{totalCompleted}</strong> completed
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadWinners} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {winnersData && winnersData.total_winners > 0 && (
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                size="small"
                onClick={() => exportCsv(winnersData.contests)}
              >
                Export CSV
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Status filter */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={filterStatus}
          exclusive
          onChange={(_, v) => { if (v !== null) setFilterStatus(v); }}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="processing">Processing</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
          <ToggleButton value="cancelled">Cancelled</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Empty state */}
      {filteredContests.length === 0 && (
        <Alert severity="info">
          {filterStatus === 'all'
            ? 'No winners recorded yet.'
            : `No winners with status "${filterStatus}".`}
        </Alert>
      )}

      {/* Contest groups */}
      {filteredContests.map(contest => (
        <Card key={contest.contest_id} sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight="bold">{contest.contest_name}</Typography>
              <Chip
                label={contest.contest_status === 'completed' ? 'Completed' : contest.contest_status}
                color={contest.contest_status === 'completed' ? 'success' : 'default'}
                size="small"
              />
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              {contest.winners.map(winner => {
                const form = editState[winner.id] ?? {
                  payout_status: winner.payout_status || 'pending',
                  payout_type:   winner.payout_type   || '',
                  payout_notes:  winner.payout_notes  || ''
                };
                const statusCfg = payoutStatusConfig[form.payout_status] ?? payoutStatusConfig.pending;
                const borderColor = winner.place === 1 ? '#FFD700' : winner.place === 2 ? '#C0C0C0' : winner.place === 3 ? '#CD7F32' : 'divider';
                const photoUrl = winner.photo_url
                  ? (getCompleteImagePath(winner.photo_url) || winner.photo_url)
                  : null;

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={winner.id}>
                    <Paper
                      elevation={winner.place <= 3 ? 4 : 2}
                      sx={{
                        p: 2.5,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        border: `2px solid`,
                        borderColor
                      }}
                    >
                      {/* Place + status + user/guest badges */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          label={`${placeLabel(winner.place)} Place`}
                          color={placeColor(winner.place)}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip
                            icon={winner.user_id ? <UserIcon fontSize="small" /> : <GuestIcon fontSize="small" />}
                            label={winner.user_id ? 'Registered' : 'Guest'}
                            color={winner.user_id ? 'info' : 'default'}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            icon={statusCfg.icon as any}
                            label={statusCfg.label}
                            color={statusCfg.color}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>

                      {/* Photo */}
                      {photoUrl && (
                        <Box
                          component="img"
                          src={photoUrl}
                          alt={winner.participant_name}
                          sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 1 }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}

                      {/* Name + stats */}
                      <Typography variant="h6" fontWeight="bold">{winner.participant_name}</Typography>
                      {(winner.participant_email || (!winner.user_id)) && (
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {winner.participant_email ?? 'No email on file'}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Votes</Typography>
                        <Typography variant="body2" fontWeight="bold">{winner.votes}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Prize</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">{formatReward(winner.reward)}</Typography>
                      </Box>
                      {winner.payout_type && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Method</Typography>
                          <Typography variant="body2">{payoutTypeLabels[winner.payout_type] ?? winner.payout_type}</Typography>
                        </Box>
                      )}
                      {winner.payout_date && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Paid on</Typography>
                          <Typography variant="body2">{new Date(winner.payout_date).toLocaleDateString()}</Typography>
                        </Box>
                      )}

                      <Divider />

                      {/* Payout form */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Payout Status</InputLabel>
                          <Select
                            value={form.payout_status}
                            label="Payout Status"
                            onChange={e => setEditState(prev => ({
                              ...prev,
                              [winner.id]: { ...form, payout_status: e.target.value as PayoutStatus }
                            }))}
                          >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth>
                          <InputLabel>Payout Type</InputLabel>
                          <Select
                            value={form.payout_type}
                            label="Payout Type"
                            onChange={e => setEditState(prev => ({
                              ...prev,
                              [winner.id]: { ...form, payout_type: e.target.value as PayoutType }
                            }))}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="gift_card">Gift Card</MenuItem>
                            <MenuItem value="rewards_points">Reward Points</MenuItem>
                          </Select>
                        </FormControl>

                        <TextField
                          size="small"
                          label="Notes"
                          multiline
                          rows={2}
                          value={form.payout_notes}
                          onChange={e => setEditState(prev => ({
                            ...prev,
                            [winner.id]: { ...form, payout_notes: e.target.value }
                          }))}
                          placeholder="e.g. Gift card code, transaction ID…"
                        />
                      </Box>

                      {/* Action buttons */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 'auto', pt: 1 }}>
                        {/* Execute Payout — the main admin action */}
                        <Tooltip title={
                          winner.payout_status === 'completed'
                            ? 'Payout already executed'
                            : !form.payout_type
                              ? 'Select a Payout Type first'
                              : `Execute ${payoutTypeLabels[form.payout_type]} payout and credit winner`
                        }>
                          <span style={{ width: '100%' }}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              fullWidth
                              startIcon={executing[winner.id] ? <CircularProgress size={14} color="inherit" /> : <ExecuteIcon />}
                              disabled={
                                executing[winner.id] ||
                                winner.payout_status === 'completed' ||
                                !form.payout_type
                              }
                              onClick={() => handleExecutePayout(winner.id)}
                            >
                              {winner.payout_status === 'completed' ? 'Payout Executed' : 'Execute Payout'}
                            </Button>
                          </span>
                        </Tooltip>

                        {winner.payout_status === 'completed' && form.payout_type && (
                          <Tooltip title="Re-send payout to pawwebapp (wallet / email / points)">
                            <span style={{ width: '100%' }}>
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                fullWidth
                                disabled={executing[winner.id]}
                                startIcon={executing[winner.id] ? <CircularProgress size={14} color="inherit" /> : <ExecuteIcon />}
                                onClick={() => handleRetryPayout(winner.id)}
                              >
                                Retry Payout in App
                              </Button>
                            </span>
                          </Tooltip>
                        )}

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={saving[winner.id] ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            disabled={saving[winner.id]}
                            onClick={() => handleSavePayout(winner.id)}
                            sx={{ flex: 1 }}
                          >
                            Save
                          </Button>
                          <Tooltip title={winner.winner_email_sent ? 'Email already sent — click to resend' : 'Send winner email'}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={resending[winner.id] ? <CircularProgress size={14} color="inherit" /> : <EmailIcon />}
                              disabled={resending[winner.id]}
                              onClick={() => handleResendEmail(winner.id)}
                              color={winner.winner_email_sent ? 'success' : 'primary'}
                            >
                              {winner.winner_email_sent ? 'Resend' : 'Email'}
                            </Button>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Container className="napc-page" maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, minHeight: '100vh' }}>
      {content}
    </Container>
  );
};

export default ContestWinnersPage;
